import React from 'react';
import { FileText, Languages, ExternalLink, Edit, Loader2, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const PagesTab = ({ 
  staticPages, 
  pagesLoading, 
  editingPage, 
  setEditingPage, 
  pageDialogOpen, 
  setPageDialogOpen,
  translateStaticPage,
  saveStaticPage,
  saveAndTranslatePage,
  saving,
  translatingPage
}) => {
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Statik Sayfalar
          </CardTitle>
          <CardDescription>
            Footer&apos;daki linklerin içeriklerini düzenleyin (Hakkımızda, KVKK, Gizlilik, vb.)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {pagesLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : (
            <div className="space-y-4">
              {staticPages.map((page) => (
                <div key={page.slug} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${page.is_active ? 'bg-green-500' : 'bg-gray-300'}`} />
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-gray-900">{page.title}</h4>
                          {page.translations && Object.keys(page.translations).length > 0 && (
                            <Badge variant="secondary" className="text-xs bg-green-100 text-green-700">
                              <Languages className="w-3 h-3 mr-1" />
                              {Object.keys(page.translations).length} dil
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">/{page.slug}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => translateStaticPage(page.slug)}
                        disabled={translatingPage}
                        className="text-green-600 hover:text-green-700 hover:bg-green-50"
                      >
                        {translatingPage ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Languages className="w-4 h-4" />
                        )}
                      </Button>
                      <a
                        href={`/${page.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 hover:bg-gray-100 rounded-lg text-gray-600 hover:text-primary transition-colors"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEditingPage({ ...page });
                          setPageDialogOpen(true);
                        }}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Düzenle
                      </Button>
                    </div>
                  </div>
                  {page.meta_description && (
                    <p className="text-xs text-gray-500 mt-2 line-clamp-1">{page.meta_description}</p>
                  )}
                </div>
              ))}
              
              {staticPages.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Henüz sayfa oluşturulmamış</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Page Edit Dialog */}
      <Dialog open={pageDialogOpen} onOpenChange={setPageDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Sayfa Düzenle: {editingPage?.title}
            </DialogTitle>
            <DialogDescription>
              Sayfa içeriğini HTML formatında düzenleyebilirsiniz.
            </DialogDescription>
          </DialogHeader>
          
          {editingPage && (
            <div className="space-y-4 py-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Sayfa Başlığı</Label>
                  <Input
                    value={editingPage.title}
                    onChange={(e) => setEditingPage({ ...editingPage, title: e.target.value })}
                    placeholder="Sayfa başlığı"
                  />
                </div>
                <div className="space-y-2">
                  <Label>URL (Slug)</Label>
                  <Input
                    value={editingPage.slug}
                    disabled
                    className="bg-gray-100"
                  />
                  <p className="text-xs text-muted-foreground">URL değiştirilemez</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Meta Açıklama (SEO)</Label>
                <Input
                  value={editingPage.meta_description || ''}
                  onChange={(e) => setEditingPage({ ...editingPage, meta_description: e.target.value })}
                  placeholder="Arama motorlarında görünecek açıklama"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Sayfa İçeriği (HTML)</Label>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-gray-600">Aktif</Label>
                    <Switch
                      checked={editingPage.is_active}
                      onCheckedChange={(v) => setEditingPage({ ...editingPage, is_active: v })}
                    />
                  </div>
                </div>
                <Textarea
                  value={editingPage.content}
                  onChange={(e) => setEditingPage({ ...editingPage, content: e.target.value })}
                  placeholder="Sayfa içeriği (HTML destekler)"
                  className="min-h-[400px] font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  HTML etiketleri kullanabilirsiniz: &lt;h1&gt;, &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;strong&gt;, &lt;em&gt;, &lt;blockquote&gt;, vb.
                </p>
              </div>

              {/* Preview */}
              <div className="space-y-2">
                <Label>Önizleme</Label>
                <div className="border rounded-lg p-4 bg-white max-h-[300px] overflow-y-auto">
                  <div 
                    className="prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: editingPage.content }}
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setPageDialogOpen(false)}>
              İptal
            </Button>
            <Button onClick={saveStaticPage} disabled={saving || translatingPage} variant="secondary">
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Kaydediliyor...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Sadece Kaydet
                </>
              )}
            </Button>
            <Button onClick={saveAndTranslatePage} disabled={saving || translatingPage} className="bg-green-600 hover:bg-green-700">
              {translatingPage ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Çevriliyor...
                </>
              ) : (
                <>
                  <Languages className="w-4 h-4 mr-2" />
                  Çevir, Kaydet & Yayınla
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PagesTab;
